import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SUPPORTED_LANGS } from '../../constants/layout';

const SITE_URL = 'https://www.kanonics.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og/cloud-native-data-driven-enterprise.png`;

const PROJECT_OG_IMAGES: Record<string, string> = {
  '/projects/digital-asset-trading-enablement': `${SITE_URL}/og/digital-asset-trading-enablement.png`,
  '/projects/digital-asset-trading-banking-clients': `${SITE_URL}/og/digital-asset-trading-banking-clients.png`,
  '/projects/data-migration': `${SITE_URL}/og/data-migration.png`,
  '/projects/core-banking-systems': `${SITE_URL}/og/core-banking-systems.png`,
  '/projects/cloud-native-data-driven-enterprise': `${SITE_URL}/og/cloud-native-data-driven-enterprise.png`,
};

const SeoHead: React.FC = () => {
  const location = useLocation();
  const lang = location.pathname.split('/')[1] || 'en';
  const currentPath = location.pathname.replace(/^\/(en|de)/, '') || '/';
  const fullUrl = `${SITE_URL}/${lang}${currentPath}`;
  const ogImage = PROJECT_OG_IMAGES[currentPath] ?? DEFAULT_OG_IMAGE;

  useEffect(() => {
    document.documentElement.lang = lang;

    // hreflang links
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    SUPPORTED_LANGS.forEach(l => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.setAttribute('hreflang', l);
      link.href = `${SITE_URL}/${l}${currentPath}`;
      document.head.appendChild(link);
    });

    const xDefault = document.createElement('link');
    xDefault.rel = 'alternate';
    xDefault.setAttribute('hreflang', 'x-default');
    xDefault.href = `${SITE_URL}/en${currentPath}`;
    document.head.appendChild(xDefault);

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = fullUrl;

    // Open Graph tags
    const updateMetaTag = (name: string, content: string, attr: string = 'name') => {
      let tag = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    updateMetaTag('og:url', fullUrl, 'property');
    updateMetaTag('og:type', 'website', 'property');
    updateMetaTag('og:site_name', 'Kanonics', 'property');
    updateMetaTag('og:image', ogImage, 'property');
    updateMetaTag('twitter:card', 'summary_large_image', 'name');
    updateMetaTag('twitter:image', ogImage, 'name');

    // JSON-LD structured data
    const removeJsonLd = () => {
      document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());
    };

    removeJsonLd();

    // Organization schema
    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Kanonics',
      url: SITE_URL,
      logo: `${SITE_URL}/kanonics-logo.png`,
      image: ogImage,
      description: 'Business, engineering, and technology partner bringing profound expertise in digital transformation, platform engineering, and disruptive technology solutions.',
      sameAs: [],
    };

    const script1 = document.createElement('script');
    script1.type = 'application/ld+json';
    script1.textContent = JSON.stringify(organizationSchema);
    document.head.appendChild(script1);

    // BreadcrumbList schema
    const breadcrumbs: Array<{ name: string; url: string }> = [{ name: 'Home', url: `${SITE_URL}/${lang}` }];

    if (currentPath === '/projects/digital-asset-trading-enablement') {
      breadcrumbs.push({ name: 'Projects', url: `${SITE_URL}/${lang}#projects` });
      breadcrumbs.push({ name: 'Digital Asset Trading Enablement', url: fullUrl });
    } else if (currentPath === '/projects/digital-asset-trading-banking-clients') {
      breadcrumbs.push({ name: 'Projects', url: `${SITE_URL}/${lang}#projects` });
      breadcrumbs.push({ name: 'Radical Transformation', url: fullUrl });
    } else if (currentPath === '/projects/data-migration') {
      breadcrumbs.push({ name: 'Projects', url: `${SITE_URL}/${lang}#projects` });
      breadcrumbs.push({ name: 'Data Migration', url: fullUrl });
    } else if (currentPath === '/projects/core-banking-systems') {
      breadcrumbs.push({ name: 'Projects', url: `${SITE_URL}/${lang}#projects` });
      breadcrumbs.push({ name: 'Core Banking Systems', url: fullUrl });
    } else if (currentPath === '/projects/cloud-native-data-driven-enterprise') {
      breadcrumbs.push({ name: 'Projects', url: `${SITE_URL}/${lang}#projects` });
      breadcrumbs.push({ name: 'Cloud-Native Trading Application', url: fullUrl });
    } else if (currentPath === '/contact') {
      breadcrumbs.push({ name: 'Contact', url: fullUrl });
    }

    if (breadcrumbs.length > 1) {
      const breadcrumbSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((crumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: crumb.name,
          item: crumb.url,
        })),
      };

      const script2 = document.createElement('script');
      script2.type = 'application/ld+json';
      script2.textContent = JSON.stringify(breadcrumbSchema);
      document.head.appendChild(script2);
    }

    return () => {
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
      removeJsonLd();
    };
  }, [lang, currentPath, fullUrl, ogImage]);

  return null;
};

export default SeoHead;
